import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Globe2, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const emptyCountry = { name: "", flag: "", tag: "", description: "", universities: "" };

export const AdminImmigration = () => {
  const [content, setContent] = useState({ facilities: [], countries: [] });
  const [loading, setLoading] = useState(true);
  const [facilityName, setFacilityName] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCountry);

  const load = async () => {
    try {
      const { data } = await api.get("/immigration/content");
      setContent(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const addFacility = async () => {
    try {
      await api.post("/admin/immigration/facilities", { name: facilityName.trim() });
      toast.success("Facility added — visible in all country modals");
      setFacilityName("");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteFacility = async (id) => {
    try {
      await api.delete(`/admin/immigration/facilities/${id}`);
      setContent((prev) => ({ ...prev, facilities: prev.facilities.filter((f) => f.id !== id) }));
      toast.success("Facility removed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addCountry = async () => {
    try {
      await api.post("/admin/immigration/countries", {
        name: form.name.trim(),
        flag: form.flag.trim() || "🌍",
        tag: form.tag.trim(),
        description: form.description.trim(),
        universities: form.universities.split(/[\n,]/).map((u) => u.trim()).filter(Boolean),
      });
      toast.success("Country added to the Immigration page");
      setOpen(false);
      setForm(emptyCountry);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteCountry = async (id) => {
    if (!window.confirm("Remove this country?")) return;
    try {
      await api.delete(`/admin/immigration/countries/${id}`);
      setContent((prev) => ({ ...prev, countries: prev.countries.filter((c) => c.id !== id) }));
      toast.success("Country removed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) {
    return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-brand-red" /></div>;
  }

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-immigration-panel">
      {/* Facilities */}
      <div className="rounded-3xl bg-white border border-brand-navy/10 p-6">
        <h3 className="font-heading font-bold text-lg text-brand-navy flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-brand-red" /> Extra facilities
        </h3>
        <p className="text-xs text-brand-navy/50 mt-1">Added on top of the 7 standard facilities, shown in every country modal.</p>
        <div className="mt-4 flex gap-2">
          <Input
            data-testid="facility-name-input"
            value={facilityName}
            onChange={(e) => setFacilityName(e.target.value)}
            placeholder="e.g. Airport Pickup Assistance"
            className="rounded-full"
          />
          <Button data-testid="facility-add-btn" onClick={addFacility} disabled={facilityName.trim().length < 2} className="rounded-full bg-brand-red hover:bg-brand-red-hover shrink-0">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <ul className="mt-4 space-y-2">
          {content.facilities.length === 0 && <li className="text-sm text-brand-navy/50">No extra facilities yet.</li>}
          {content.facilities.map((f) => (
            <li key={f.id} data-testid={`facility-row-${f.id}`} className="flex items-center justify-between bg-emerald-50 border border-emerald-500/20 rounded-2xl px-4 py-2.5 text-sm font-semibold text-brand-navy">
              {f.name}
              <button data-testid={`delete-facility-${f.id}`} onClick={() => deleteFacility(f.id)} className="text-brand-red/70 hover:text-brand-red" aria-label="Delete facility">
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Countries */}
      <div className="rounded-3xl bg-white border border-brand-navy/10 p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg text-brand-navy flex items-center gap-2">
            <Globe2 className="w-5 h-5 text-brand-red" /> Extra countries
          </h3>
          <Button data-testid="new-country-btn" onClick={() => setOpen(true)} size="sm" className="rounded-full bg-brand-red hover:bg-brand-red-hover">
            <Plus className="w-4 h-4 mr-1" /> Add country
          </Button>
        </div>
        <p className="text-xs text-brand-navy/50 mt-1">Shown alongside the 6 default destinations on the Immigration page.</p>
        <ul className="mt-4 space-y-2">
          {content.countries.length === 0 && <li className="text-sm text-brand-navy/50">No extra countries yet.</li>}
          {content.countries.map((c) => (
            <li key={c.id} data-testid={`country-row-${c.id}`} className="flex items-center justify-between bg-brand-muted-bg rounded-2xl px-4 py-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-2xl">{c.flag || "🌍"}</span>
                <div className="min-w-0">
                  <div className="font-semibold text-brand-navy text-sm truncate">{c.name}</div>
                  <div className="text-xs text-brand-navy/50 truncate">{c.tag || "—"} · {(c.universities || []).length} universities</div>
                </div>
              </div>
              <button data-testid={`delete-country-${c.id}`} onClick={() => deleteCountry(c.id)} className="text-brand-red/70 hover:text-brand-red shrink-0 ml-2" aria-label="Delete country">
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-brand-navy">New country</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-[1fr_90px] gap-3">
              <div><Label>Country name</Label><Input data-testid="country-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ireland" /></div>
              <div><Label>Flag</Label><Input data-testid="country-form-flag" value={form.flag} onChange={(e) => setForm({ ...form, flag: e.target.value })} placeholder="🇮🇪" /></div>
            </div>
            <div><Label>Tagline</Label><Input data-testid="country-form-tag" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} placeholder="2-year stay-back · EU career gateway" /></div>
            <div><Label>Description</Label><Textarea data-testid="country-form-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Universities (one per line or comma-separated)</Label><Textarea data-testid="country-form-universities" rows={3} value={form.universities} onChange={(e) => setForm({ ...form, universities: e.target.value })} placeholder={"Trinity College Dublin\nUniversity College Dublin"} /></div>
            <Button
              data-testid="country-form-save"
              onClick={addCountry}
              disabled={form.name.trim().length < 2}
              className="w-full rounded-full bg-brand-red hover:bg-brand-red-hover"
            >
              Add country
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
